/* eslint-disable react/display-name */
import React from 'react';
import {
	GridColDef,
	GridColumnGroup,
	GridEditInputCell,
} from '@mui/x-data-grid-premium';
import { useGlobalization } from '@Translations/useGlobalization';
import { Component } from '@Types';
import { getRangeDisplayTitle } from '../ScoreDataGrid/RangeCell';
import { getFlex, getWidth } from '../ScoreDataGrid/TabelColumns';
import { GridInitialState } from '@innovyze/stylovyze';
import { SkeletonCell } from '../ScoreDataGrid/SkeletonCell';
import { useSelectIsCountLoading } from '@Selectors';

export const tableColumns = (
	componentParameter1: Component,
	componentParameter2: Component,
	countKey: string,
	dataGridState?: GridInitialState,
) => {
	const { t } = useGlobalization();
	const header = new Array<GridColDef>();
	const scoreMax = 1000;

	const isRiskCountLoading = useSelectIsCountLoading(countKey);

	if (componentParameter2.metadataType === 'unique') {
		header.push({
			field: 'param2',
			headerName: componentParameter2.name,
			sortable: false,
			hideable: false,
			align: 'left',
			flex: getFlex('param2', 1, dataGridState),
			width: getWidth('param2', dataGridState),
			minWidth: 200,
			cellClassName: 'titleCell',
		});
	} else {
		header.push({
			field: 'param2',
			headerName: componentParameter2.name,
			sortable: false,
			hideable: false,
			align: 'left',
			flex: getFlex('param2', 1, dataGridState),
			width: getWidth('param2', dataGridState),
			minWidth: 200,
			cellClassName: 'titleCell',
			renderCell: params => {
				return getRangeDisplayTitle(
					params.value ?? '',
					componentParameter2.unitMetadata?.unit,
					componentParameter2.unitMetadata?.currency,
				);
			},
		});
	}

	if (componentParameter1.metadata) {
		for (let i = 0; i < componentParameter1.metadata.length; i++) {
			header.push(
				{
					field: `${i}-score`,
					headerName: t('Score'),
					sortable: false,
					hideable: false,
					align: 'center',
					headerAlign: 'center',
					flex: getFlex(`${i}-score`, 0.5, dataGridState),
					width: getWidth(`${i}-score`, dataGridState),
					minWidth: 100,
					type: 'number',
					editable: true,
					cellClassName: i == 0 ? '' : 'scoreCell',
					valueFormatter: params => {
						// Stop the default formatting adding , to numbers
						return params.value?.toString();
					},
					renderEditCell: params => (
						<GridEditInputCell
							{...params}
							inputProps={{
								max: scoreMax,
								min: 0,
								disabled: isRiskCountLoading,
							}}
						/>
					),
				},
				{
					field: `${i}-count`,
					headerName: t('Count'),
					sortable: false,
					hideable: false,
					align: 'center',
					headerAlign: 'center',
					flex: getFlex(`${i}-count`, 0.5, dataGridState),
					width: getWidth(`${i}-count`, dataGridState),
					minWidth: 100,
					renderCell: params => {
						return (
							<SkeletonCell
								id={params.id}
								countKey={countKey}
								value={params.value}
								percent={false}
							/>
						);
					},
				},
			);
		}
	}

	return header;
};

export const tableColumnsGrouping = (componentParameter1: Component) => {
	const headerGroup = new Array<GridColumnGroup>();

	if (componentParameter1.metadata) {
		headerGroup.push({
			groupId: componentParameter1.name,
			headerAlign: 'right',
			children: [{ field: 'param2' }],
		});

		const range = componentParameter1.metadataType === 'range';
		for (let i = 0; i < componentParameter1.metadata.length; i++) {
			const field = componentParameter1.metadata[i];

			const headerName = range
				? getRangeDisplayTitle(
						field.key,
						componentParameter1.unitMetadata?.unit,
						componentParameter1.unitMetadata?.currency,
				  )
				: field.key;

			headerGroup.push({
				groupId: `group-${i}`,
				headerName: headerName,
				headerAlign: 'center',
				children: [{ field: `${i}-score` }, { field: `${i}-count` }],
			});
		}
	}

	return headerGroup;
};
